import React, { useState, useEffect } from "react";
import {
    Modal,
    Button,
    FormControl,
    Table,
    Alert,
    Glyphicon,
    Label
} from "react-bootstrap";
import { getShippingConfirmationRecipients, sendShippingConfirmation } from "../utils/bedrockAPI";

export default function ShippingConfirmationModal({ show, onHide, orders, onSent }) {
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [confirmationType, setConfirmationType] = useState('standard');
    const [groups, setGroups] = useState([]);
    const [optionalMessage, setOptionalMessage] = useState('');

    const orderIds = orders.map(order => order.orderId);
    const ordersMissingTracking = orders.filter(order => !order.cap?.tracking);

    useEffect(() => {
        if (show && orderIds.length > 0) {
            loadRecipients();
        } else if (!show) {
            setGroups([]);
            setError(null);
            setSuccess(false);
            setConfirmationType('standard');
            setOptionalMessage('');
        }
    }, [show]);

    const loadRecipients = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getShippingConfirmationRecipients(orderIds);
            setConfirmationType(data.confirmationType || 'standard');
            setGroups((data.groups || []).map(group => ({
                builderId: group.builderId,
                recipients: (group.recipients || []).map(recipient => ({
                    email: recipient.email || '',
                    firstName: recipient.firstName || '',
                    lastName: recipient.lastName || '',
                    recipientType: recipient.recipientType || 'to',
                    source: recipient.source
                }))
            })));
        } catch (err) {
            console.error('Error loading shipping confirmation recipients:', err);
            setError(err?.response?.data?.error?.message || 'Failed to load recipients. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getBuilderName = (builderId) => {
        const order = orders.find(o => o.builderId === builderId);
        return order?.builder?.name || builderId;
    };

    const getSourceStyle = (source) => {
        if (source === 'admin') return 'success';
        if (['stripe', 'purchaser', 'installer', 'manager'].includes(source)) return 'info';
        return 'default';
    };

    const updateRecipient = (groupIndex, recipientIndex, field, value) => {
        setGroups(prev => prev.map((group, gi) => {
            if (gi !== groupIndex) return group;
            return {
                ...group,
                recipients: group.recipients.map((recipient, ri) =>
                    ri === recipientIndex ? { ...recipient, [field]: value } : recipient
                )
            };
        }));
    };

    const addRecipient = (groupIndex) => {
        setGroups(prev => prev.map((group, gi) => {
            if (gi !== groupIndex) return group;
            return {
                ...group,
                recipients: [
                    ...group.recipients,
                    { email: '', firstName: '', lastName: '', recipientType: 'cc', source: 'manual' }
                ]
            };
        }));
    };

    const removeRecipient = (groupIndex, recipientIndex) => {
        setGroups(prev => prev.map((group, gi) => {
            if (gi !== groupIndex) return group;
            return {
                ...group,
                recipients: group.recipients.filter((_, ri) => ri !== recipientIndex)
            };
        }));
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const getValidationError = () => {
        for (const group of groups) {
            const builderName = getBuilderName(group.builderId);
            for (const recipient of group.recipients) {
                if (!isValidEmail(recipient.email.trim())) {
                    return `Invalid email "${recipient.email}" for ${builderName}.`;
                }
            }
            if (!group.recipients.some(r => r.recipientType === 'to')) {
                return `At least one "To" recipient is required for ${builderName}.`;
            }
        }
        if (groups.length === 0) {
            return 'No recipients to send to.';
        }
        return null;
    };

    const handleSend = async () => {
        const validationError = getValidationError();
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setSending(true);
            setError(null);

            const recipients = groups.flatMap(group =>
                group.recipients.map(recipient => ({
                    email: recipient.email.trim(),
                    firstName: recipient.firstName.trim() || undefined,
                    lastName: recipient.lastName.trim() || undefined,
                    recipientType: recipient.recipientType,
                    builderId: group.builderId,
                    source: recipient.source
                }))
            );

            await sendShippingConfirmation(orderIds, recipients, optionalMessage.trim() || undefined);

            setSuccess(true);
            setTimeout(() => {
                if (onSent) {
                    onSent();
                }
                onHide();
            }, 1500);
        } catch (err) {
            console.error('Error sending shipping confirmation:', err);
            setError(err?.response?.data?.error?.message || 'Failed to send shipping confirmation emails. Please try again.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} bsSize="large">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontFamily: 'inherit', fontSize: '18px', fontWeight: 'normal' }}>
                    <Glyphicon glyph="envelope" style={{ marginRight: "8px" }} />
                    {confirmationType === 'return-boxes' ? 'Send Return Box Confirmation' :
                     confirmationType === 'replacement' ? 'Send Replacement Confirmation' :
                     'Send Shipping Confirmation'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && (
                    <Alert bsStyle="danger">
                        <Glyphicon glyph="exclamation-sign" /> {error}
                    </Alert>
                )}

                {success && (
                    <Alert bsStyle="success">
                        <Glyphicon glyph="ok" /> Shipping confirmation email(s) sent successfully!
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center" style={{ padding: '20px' }}>
                        <Glyphicon glyph="refresh" className="spinning" /> Loading recipients...
                    </div>
                ) : (
                    <div>
                        <Alert bsStyle={confirmationType === 'standard' ? 'info' : 'warning'}>
                            {confirmationType === 'return-boxes' ? (
                                <span>
                                    <strong>RETURN BOX email:</strong> all selected order(s) have RETURNING
                                    status, so recipients will receive a return box email instead of the
                                    standard or replacement shipping email.
                                </span>
                            ) : confirmationType === 'replacement' ? (
                                <span>
                                    <strong>REPLACEMENT email:</strong> the selected order(s) have REPLACEMENT
                                    status, so recipients will receive a replacement shipping email.
                                </span>
                            ) : (
                                <span>
                                    <strong>Standard shipping email:</strong> recipients will receive a normal
                                    shipping confirmation email.
                                </span>
                            )}
                            {' '}Sending for {orderIds.length} order{orderIds.length === 1 ? '' : 's'}.
                        </Alert>

                        {ordersMissingTracking.length > 0 && (
                            <Alert bsStyle="danger">
                                <Glyphicon glyph="warning-sign" />{' '}
                                <strong>Missing tracking number{ordersMissingTracking.length === 1 ? '' : 's'}:</strong>{' '}
                                {ordersMissingTracking
                                    .map(order => order.cap?.name || `Order ${order.orderId}`)
                                    .join(', ')}
                                {' '}� the shipping confirmation will fail without tracking numbers.
                            </Alert>
                        )}

                        {groups.map((group, groupIndex) => (
                            <div key={group.builderId} style={{ marginBottom: '20px' }}>
                                <h4 style={{ fontFamily: 'inherit', fontSize: '16px', marginBottom: '10px' }}>
                                    {getBuilderName(group.builderId)}
                                </h4>
                                <Table condensed bordered>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '32%' }}>Email</th>
                                            <th style={{ width: '18%' }}>First Name</th>
                                            <th style={{ width: '18%' }}>Last Name</th>
                                            <th style={{ width: '12%' }}>Type</th>
                                            <th style={{ width: '12%' }}>Source</th>
                                            <th style={{ width: '8%' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.recipients.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center text-muted">
                                                    No recipients. Add at least one "To" recipient.
                                                </td>
                                            </tr>
                                        )}
                                        {group.recipients.map((recipient, recipientIndex) => (
                                            <tr key={recipientIndex}>
                                                <td>
                                                    <FormControl
                                                        type="email"
                                                        bsSize="sm"
                                                        value={recipient.email}
                                                        placeholder="email@example.com"
                                                        onChange={(e) => updateRecipient(groupIndex, recipientIndex, 'email', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControl
                                                        type="text"
                                                        bsSize="sm"
                                                        value={recipient.firstName}
                                                        placeholder="First"
                                                        disabled={recipient.recipientType === 'cc'}
                                                        onChange={(e) => updateRecipient(groupIndex, recipientIndex, 'firstName', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControl
                                                        type="text"
                                                        bsSize="sm"
                                                        value={recipient.lastName}
                                                        placeholder="Last"
                                                        disabled={recipient.recipientType === 'cc'}
                                                        onChange={(e) => updateRecipient(groupIndex, recipientIndex, 'lastName', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControl
                                                        componentClass="select"
                                                        bsSize="sm"
                                                        value={recipient.recipientType}
                                                        onChange={(e) => updateRecipient(groupIndex, recipientIndex, 'recipientType', e.target.value)}
                                                    >
                                                        <option value="to">To</option>
                                                        <option value="cc">Cc</option>
                                                    </FormControl>
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {recipient.source && (
                                                        <Label bsStyle={getSourceStyle(recipient.source)}>
                                                            {recipient.source}
                                                        </Label>
                                                    )}
                                                </td>
                                                <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                                    <Button
                                                        bsSize="xs"
                                                        bsStyle="danger"
                                                        onClick={() => removeRecipient(groupIndex, recipientIndex)}
                                                        title="Remove recipient"
                                                    >
                                                        <Glyphicon glyph="remove" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <Button bsSize="sm" onClick={() => addRecipient(groupIndex)}>
                                    <Glyphicon glyph="plus" /> Add Recipient
                                </Button>
                            </div>
                        ))}

                        {groups.length === 0 && !error && (
                            <div className="text-center text-muted" style={{ padding: '20px' }}>
                                No recipients found for the selected orders.
                            </div>
                        )}

                        {groups.length > 0 && (
                            <div style={{ marginTop: '10px' }}>
                                <label style={{ fontWeight: 'bold' }}>Optional Message</label>
                                <FormControl
                                    componentClass="textarea"
                                    rows={3}
                                    value={optionalMessage}
                                    placeholder="Add an optional message to include in the email..."
                                    onChange={(e) => setOptionalMessage(e.target.value)}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button onClick={onHide} disabled={sending}>
                    Cancel
                </Button>
                <Button
                    bsStyle="success"
                    onClick={handleSend}
                    disabled={loading || sending || success || groups.length === 0}
                >
                    {sending ? (
                        <span>
                            <Glyphicon glyph="refresh" className="spinning" /> Sending...
                        </span>
                    ) : (
                        <span>
                            <Glyphicon glyph="envelope" /> Confirm Send
                        </span>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
